import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { metersAPI, unitsAPI, propertiesAPI, referenceAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Gauge,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Activity,
} from 'lucide-react';

export default function Meters() {
  const [meters, setMeters] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [meterTypes, setMeterTypes] = useState([]);
  const [meterUnits, setMeterUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [expandedMeterId, setExpandedMeterId] = useState(null);
  const [readingsByMeter, setReadingsByMeter] = useState({});
  const [deletingMeter, setDeletingMeter] = useState(null);

  // Create form
  const [form, setForm] = useState({
    unit_id: '',
    meter_type: 'wasser',
    meter_number: '',
    unit_of_measure: 'm3',
    notes: '',
  });

  // Reading dialog
  const [showReadingDialog, setShowReadingDialog] = useState(false);
  const [readingForm, setReadingForm] = useState({
    meter_id: '',
    reading_date: new Date().toISOString().slice(0, 10),
    reading_value: '',
    notes: '',
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [mRes, uRes, pRes, mtRes, muRes] = await Promise.all([
        metersAPI.getAll({}),
        unitsAPI.getAll(),
        propertiesAPI.getAll(),
        referenceAPI.getMeterTypes(),
        referenceAPI.getMeterUnits(),
      ]);
      setMeters(mRes.data);
      setUnits(uRes.data);
      setProperties(pRes.data);
      setMeterTypes(mtRes.data);
      setMeterUnits(muRes.data);
    } catch (e) {
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const unitMap = Object.fromEntries(units.map((u) => [u.id, u]));
  const propertyMap = Object.fromEntries(properties.map((p) => [p.id, p]));
  const meterTypeLabel = (v) => meterTypes.find((m) => m.value === v)?.label || v;
  const meterUnitLabel = (v) => meterUnits.find((m) => m.value === v)?.label || v;

  const filtered = meters.filter((m) =>
    propertyFilter === 'all' ? true : m.property_id === propertyFilter
  );

  const toggleExpand = async (meterId) => {
    if (expandedMeterId === meterId) {
      setExpandedMeterId(null);
      return;
    }
    setExpandedMeterId(meterId);
    if (!readingsByMeter[meterId]) {
      try {
        const r = await metersAPI.listReadings(meterId);
        setReadingsByMeter((prev) => ({ ...prev, [meterId]: r.data }));
      } catch (e) {
        toast.error('Ablesungen konnten nicht geladen werden');
      }
    }
  };

  const reloadReadings = async (meterId) => {
    const r = await metersAPI.listReadings(meterId);
    setReadingsByMeter((prev) => ({ ...prev, [meterId]: r.data }));
  };

  const createMeter = async (e) => {
    e.preventDefault();
    if (!form.unit_id) { toast.error('Bitte Einheit wählen'); return; }
    try {
      await metersAPI.create(form);
      toast.success('Zähler angelegt');
      setShowCreate(false);
      setForm({ unit_id: '', meter_type: 'wasser', meter_number: '', unit_of_measure: 'm3', notes: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const addReading = async (e) => {
    e.preventDefault();
    if (!readingForm.reading_value) { toast.error('Bitte Wert angeben'); return; }
    try {
      await metersAPI.addReading(readingForm.meter_id, {
        reading_date: readingForm.reading_date,
        reading_value: parseFloat(readingForm.reading_value),
        notes: readingForm.notes || null,
      });
      toast.success('Ablesung gespeichert');
      setShowReadingDialog(false);
      await reloadReadings(readingForm.meter_id);
      setReadingForm({ ...readingForm, reading_value: '', notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const deleteReading = async (readingId, meterId) => {
    try {
      await metersAPI.deleteReading(readingId);
      toast.success('Ablesung gelöscht');
      await reloadReadings(meterId);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const confirmDeleteMeter = async () => {
    try {
      await metersAPI.delete(deletingMeter.id);
      toast.success('Zähler gelöscht');
      setDeletingMeter(null);
      loadAll();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const computeConsumption = (readings) => {
    if (!readings || readings.length < 2) return null;
    const sorted = [...readings].sort((a, b) => a.reading_date.localeCompare(b.reading_date));
    return (sorted[sorted.length - 1].reading_value - sorted[0].reading_value).toFixed(2);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="meters-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Zähler & Ablesungen
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Verwalten Sie Wasser-, Heizungs-, Strom- und Gaszähler pro Einheit. Verbrauch wird automatisch für die verbrauchsbasierte Abrechnung herangezogen.
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="add-meter-button" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" /> Zähler anlegen
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-meter-dialog">
              <DialogHeader>
                <DialogTitle>Neuen Zähler anlegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={createMeter} className="space-y-4">
                <div>
                  <Label>Einheit *</Label>
                  <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger data-testid="meter-unit-select"><SelectValue placeholder="Einheit wählen" /></SelectTrigger>
                    <SelectContent>
                      {units.map((u) => {
                        const p = propertyMap[u.property_id];
                        return (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} {p ? `– ${p.name}` : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Zählertyp *</Label>
                    <Select value={form.meter_type} onValueChange={(v) => setForm({ ...form, meter_type: v })}>
                      <SelectTrigger data-testid="meter-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {meterTypes.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Einheit *</Label>
                    <Select value={form.unit_of_measure} onValueChange={(v) => setForm({ ...form, unit_of_measure: v })}>
                      <SelectTrigger data-testid="meter-unit-of-measure"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {meterUnits.map((u) => (
                          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Zählernummer (optional)</Label>
                  <Input data-testid="meter-number-input" value={form.meter_number} onChange={(e) => setForm({ ...form, meter_number: e.target.value })} />
                </div>
                <div>
                  <Label>Notiz (optional)</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
                  <Button type="submit" data-testid="meter-submit-button" className="bg-emerald-600 hover:bg-emerald-700">Anlegen</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Label className="min-w-fit">Immobilie filtern:</Label>
              <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                <SelectTrigger className="w-full sm:w-72" data-testid="meter-property-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Immobilien</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Gauge className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600">Noch keine Zähler vorhanden.</p>
              <p className="text-sm text-slate-500 mt-1">Legen Sie einen Zähler pro Einheit und Verbrauchsart an, um Verbrauchskosten automatisch zu verteilen.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((m) => {
              const unit = unitMap[m.unit_id];
              const prop = unit ? propertyMap[unit.property_id] : null;
              const readings = readingsByMeter[m.id];
              const consumption = computeConsumption(readings);
              const expanded = expandedMeterId === m.id;
              return (
                <Card key={m.id} data-testid={`meter-row-${m.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <button
                        type="button"
                        className="flex items-start gap-3 text-left flex-1"
                        onClick={() => toggleExpand(m.id)}
                        data-testid={`meter-expand-${m.id}`}
                      >
                        {expanded ? <ChevronDown className="h-5 w-5 mt-1 text-slate-400" /> : <ChevronRight className="h-5 w-5 mt-1 text-slate-400" />}
                        <Gauge className="h-5 w-5 mt-1 text-emerald-600" />
                        <div>
                          <div className="font-medium text-slate-900">
                            {meterTypeLabel(m.meter_type)}
                            {m.meter_number && <span className="text-slate-500 ml-2">#{m.meter_number}</span>}
                          </div>
                          <div className="text-sm text-slate-500">
                            {unit?.name || 'Unbekannte Einheit'} · {prop?.name || ''} · Einheit: {meterUnitLabel(m.unit_of_measure)}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{readings?.length ?? '–'} Ablesungen</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setReadingForm({
                              meter_id: m.id,
                              reading_date: new Date().toISOString().slice(0, 10),
                              reading_value: '',
                              notes: '',
                            });
                            setShowReadingDialog(true);
                          }}
                          data-testid={`add-reading-${m.id}`}
                        >
                          <Activity className="h-4 w-4 mr-1" /> Ablesung
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeletingMeter(m)}
                          data-testid={`delete-meter-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>

                    {expanded && (
                      <div className="mt-4 pl-12 border-l-2 border-slate-100">
                        {consumption && (
                          <div className="mb-3 text-sm text-slate-600">
                            Verbrauch über alle Ablesungen: <span className="font-medium text-slate-900">{consumption} {meterUnitLabel(m.unit_of_measure)}</span>
                          </div>
                        )}
                        {(!readings || readings.length === 0) ? (
                          <p className="text-sm text-slate-500 py-3">Noch keine Ablesungen erfasst.</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Datum</TableHead>
                                <TableHead>Wert</TableHead>
                                <TableHead>Notiz</TableHead>
                                <TableHead></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {readings.map((r) => (
                                <TableRow key={r.id} data-testid={`reading-row-${r.id}`}>
                                  <TableCell>{r.reading_date}</TableCell>
                                  <TableCell className="font-mono">{r.reading_value} {meterUnitLabel(m.unit_of_measure)}</TableCell>
                                  <TableCell className="text-slate-600">{r.notes || '–'}</TableCell>
                                  <TableCell>
                                    <Button variant="ghost" size="sm" onClick={() => deleteReading(r.id, m.id)} data-testid={`delete-reading-${r.id}`}>
                                      <Trash2 className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Reading dialog */}
        <Dialog open={showReadingDialog} onOpenChange={setShowReadingDialog}>
          <DialogContent data-testid="add-reading-dialog">
            <DialogHeader>
              <DialogTitle>Ablesung erfassen</DialogTitle>
            </DialogHeader>
            <form onSubmit={addReading} className="space-y-4">
              <div>
                <Label>Ablesedatum *</Label>
                <Input type="date" value={readingForm.reading_date} onChange={(e) => setReadingForm({ ...readingForm, reading_date: e.target.value })} data-testid="reading-date-input" />
              </div>
              <div>
                <Label>Zählerstand *</Label>
                <Input type="number" step="0.01" min="0" value={readingForm.reading_value} onChange={(e) => setReadingForm({ ...readingForm, reading_value: e.target.value })} data-testid="reading-value-input" placeholder="z. B. 1234.56" />
              </div>
              <div>
                <Label>Notiz (optional)</Label>
                <Textarea value={readingForm.notes} onChange={(e) => setReadingForm({ ...readingForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowReadingDialog(false)}>Abbrechen</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="reading-submit">Speichern</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete confirm */}
        <AlertDialog open={!!deletingMeter} onOpenChange={(o) => !o && setDeletingMeter(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zähler löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Alle Ablesungen dieses Zählers werden ebenfalls gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteMeter} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-meter">Löschen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
